import { Box, chakra, Text } from '@chakra-ui/react'
import { DateTime, Duration } from 'luxon'

import { pluralize } from '@/utils/helpers'

import { useRelativeTimeShort } from '@/client/hooks'
import {
  AUTO_CANCEL_LIVE_HELP_AFTER_OFFLINE_FOR_MINUTES,
  AUTO_CLOSE_POST_AFTER_NO_RESPONSE_FOR_HOURS,
  type PostEventType,
} from '@/client/services/worlds'

import type { PostDetails, PostEventDetails } from '@/components/Community/Post/types'
import Tippy from '@/components/UI/Tippy'
import DisplayNameLink from '@/components/Worlds/UI/DisplayNameLink'

type NonCommentPostEventType = Exclude<PostEventType, 'comment'>

const POST_EVENT_TYPE_TO_DISPLAY_INFO: Record<NonCommentPostEventType, { label: string }> = {
  'mark-solved': {
    label: 'marked the question as Solved',
  },
  'mark-not-solved': {
    label: 'marked the question as Not Solved',
  },
  'request-live-help': {
    label: 'requested Zoom help',
  },
  'cancel-live-help-request': {
    label: 'canceled their request for Zoom help',
  },
  'open-live-help-session': {
    label: 'opened a Zoom room',
  },
  'tutor-canceled-giving-live-help': {
    label: 'closed their Zoom help room',
  },
  'learner-left-live-help-session': {
    label: 'were in a Zoom room',
  },
  'auto-cancel-live-help-request': {
    label: `has been inactive for more than ${pluralize(
      AUTO_CANCEL_LIVE_HELP_AFTER_OFFLINE_FOR_MINUTES,
      'minute'
    )}, so their request for Zoom help has automatically been closed`,
  },
  'auto-close-post': {
    label: `has not responded for more than ${pluralize(
      AUTO_CLOSE_POST_AFTER_NO_RESPONSE_FOR_HOURS,
      'hour'
    )}, so their post has automatically been closed`,
  },
  'mark-closed': {
    label: 'marked the question as Closed',
  },
}

export default function PostEventUserAction({
  post,
  postEvent,
}: {
  post: PostDetails
  postEvent: PostEventDetails
}): JSX.Element | null {
  const { user, createdAt, type, payload } = postEvent

  const createdAtRelative = useRelativeTimeShort({ time: createdAt })

  if (type === 'comment') {
    throw new Error(`Unexpected Post Event type ${type}`)
  }

  const postAuthor = post.user
  const { id: userId, displayName, username } = user
  const displayInfo = POST_EVENT_TYPE_TO_DISPLAY_INFO[type]
  const durationExists = payload?.type === 'learner-left-live-help-session'
  const durationMins = durationExists
    ? Duration.fromObject({
        seconds: payload.duration,
      }).as('minutes')
    : 0

  return (
    <Box textAlign="center" _hover={{ bg: 'bg.subtle' }} px="24px" py="8px">
      <Text variant="supporting" color="fg.muted">
        <DisplayNameLink userId={userId} displayName={displayName} username={username} />
        {type === 'learner-left-live-help-session' ? (
          <>
            {' and '}
            <DisplayNameLink
              userId={postAuthor.id}
              displayName={postAuthor.displayName}
              username={postAuthor.username}
            />
            {` ${displayInfo.label}`}
            {durationExists ? ` for ${pluralize(Math.ceil(durationMins), 'minute')}` : null}
          </>
        ) : (
          ` ${displayInfo.label}`
        )}
        {' · '}
        <Tippy content={createdAt.toLocaleString(DateTime.DATETIME_FULL)}>
          <chakra.span>{createdAtRelative}</chakra.span>
        </Tippy>
      </Text>
    </Box>
  )
}
